import React, { useState, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { AppSwitcher } from './BrokerConsole';

const Navigation = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Primary destinations, ordered most-used first so the LEAST-used overflow into "More".
  const navItems = [];
  navItems.push({ path: '/', label: 'Home' });
  if (['super_admin', 'broker', 'agent'].includes(user?.role)) {
    navItems.push({ path: '/buyers', label: 'Clients' });
    navItems.push({ path: '/properties', label: 'Properties' });
    navItems.push({ path: '/agent-board', label: 'Master Board' });
  }
  if (['super_admin', 'broker', 'agent', 'buyer'].includes(user?.role)) navItems.push({ path: '/match-board', label: 'Match Board' });
  if (['super_admin', 'broker', 'agent'].includes(user?.role)) {
    navItems.push({ path: '/deals', label: 'Deals' });
    navItems.push({ path: '/reports', label: '📄 Reports' });
  }
  if (user?.role === 'buyer' && user?.buyer_id) navItems.push({ path: '/buyers/' + user.buyer_id + '/criteria', label: 'My Criteria' });

  // Admin-type destinations collapse into a single "Admin" dropdown (kept always visible).
  let adminItems = [];
  if (user?.role === 'super_admin') {
    adminItems = [
      { path: '/users', label: 'Users' },
      { path: '/brokerages', label: 'Brokerages' },
      { path: '/associations', label: 'Associations' },
      { path: '/reports/aida-revenue', label: 'AiDA Revenue' },
    ];
  } else if (user?.role === 'broker') {
    adminItems.push({ path: '/users', label: 'Users' });
    if (user?.account_type === 'brokerage_user') adminItems.push({ path: '/brokerage', label: 'Brokerage Admin' });
  }

  const isActive = (path) => location.pathname === path;
  const linkCls = (path) => `px-2 py-2 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors ${isActive(path) ? 'text-[#00D4FF] bg-[#243B55]' : 'text-[#B0BEC5] hover:text-white hover:bg-[#243B55]'}`;
  const mobileCls = (path) => `block px-3 py-2.5 rounded-md text-sm font-medium ${isActive(path) ? 'text-[#00D4FF] bg-[#243B55]' : 'text-[#B0BEC5] hover:text-white hover:bg-[#243B55]'}`;

  // --- Priority overflow: measure links against available width; the ones that don't fit
  // move into a "More" dropdown. The bar NEVER collapses to the hamburger at >=1024px. ---
  const rowRef = useRef(null);
  const ghostRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(navItems.length);
  const itemKey = navItems.map((n) => n.path).join('|');

  useLayoutEffect(() => {
    const row = rowRef.current;
    const ghost = ghostRef.current;
    if (!row || !ghost) return;
    const GAP = 4; // gap-1
    const SAFETY = 8;
    const compute = () => {
      const avail = row.clientWidth;
      if (!avail) return; // hidden (below lg) — hamburger handles it
      const widths = Array.from(ghost.querySelectorAll('[data-gl]')).map((el) => el.getBoundingClientRect().width);
      const moreEl = ghost.querySelector('[data-gm]');
      const moreW = moreEl ? moreEl.getBoundingClientRect().width : 60;
      const totalAll = widths.reduce((a, w) => a + w + GAP, 0) - GAP;
      if (totalAll <= avail) { setVisibleCount(widths.length); return; }
      const budget = avail - moreW - GAP - SAFETY;
      let used = 0, count = 0;
      for (let i = 0; i < widths.length; i++) {
        const next = used + widths[i] + (count > 0 ? GAP : 0);
        if (next <= budget) { used = next; count++; } else break;
      }
      setVisibleCount(Math.max(1, count));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(row);
    window.addEventListener('resize', compute);
    return () => { ro.disconnect(); window.removeEventListener('resize', compute); };
  }, [itemKey]);

  const visibleItems = navItems.slice(0, visibleCount);
  const overflowItems = navItems.slice(visibleCount);

  const getRoleBadge = (role, small) => {
    const colors = {
      super_admin: 'bg-purple-600 text-purple-100', broker: 'bg-blue-600 text-blue-100',
      agent: 'bg-green-600 text-green-100', buyer: 'bg-cyan-600 text-cyan-100', demo: 'bg-orange-600 text-orange-100',
    };
    const labels = { super_admin: 'Super Admin', broker: 'Broker', agent: 'Agent', buyer: 'Client', demo: 'Demo' };
    return <span className={`${small ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'} rounded font-medium whitespace-nowrap ${colors[role] || 'bg-gray-600 text-gray-100'}`}>{labels[role] || role}</span>;
  };

  const isDemo = user?.role === 'demo';

  const Dropdown = ({ open, setOpen, button, children, width = 'w-44' }) => (
    <div className="relative shrink-0">
      <button onClick={() => setOpen(!open)}>{button}</button>
      {open && (
        <div className={`absolute right-0 top-11 ${width} bg-[#1A2744] border border-[#546E7A]/50 rounded-lg shadow-xl z-50 overflow-hidden`}>{children}</div>
      )}
    </div>
  );

  return (
    <React.Fragment>
      {isDemo && (
        <div className="bg-orange-600 text-white text-center py-1.5 px-3 text-sm font-medium">DEMO MODE — View only. Contact AiDA Advisory for full access.</div>
      )}
      <nav className="bg-[#1A2744] border-b border-[#546E7A]/30">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16 gap-2">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity shrink-0">
              <div className="text-lg"><span className="font-bold text-white">Scheme</span><span className="font-bold text-[#00D4FF]">Sync</span><span className="font-bold text-[#FF6B35] ml-1 text-sm">CRE</span></div>
            </Link>

            {/* Desktop links region (lg+): visible links + overflow "More". Never wraps; clips as a last resort. */}
            <div ref={rowRef} className="hidden lg:flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
              {visibleItems.map((item) => <Link key={item.path} to={item.path} className={linkCls(item.path)}>{item.label}</Link>)}
              {overflowItems.length > 0 && (
                <Dropdown open={moreOpen} setOpen={setMoreOpen} width="w-48"
                  button={<span className={`${linkCls('/__more')} flex items-center gap-1`}>More <span className="text-[10px]">▾</span></span>}>
                  {overflowItems.map((it) => <Link key={it.path} to={it.path} onClick={() => setMoreOpen(false)} className={mobileCls(it.path)}>{it.label}</Link>)}
                </Dropdown>
              )}
            </div>

            {/* Desktop right cluster (lg+): Admin dropdown, compact switcher, account dropdown. */}
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              {adminItems.length > 0 && (
                <Dropdown open={adminOpen} setOpen={setAdminOpen}
                  button={<span className={`${linkCls('/__admin')} flex items-center gap-1`}>Admin <span className="text-[10px]">▾</span></span>}>
                  {adminItems.map((it) => <Link key={it.path} to={it.path} onClick={() => setAdminOpen(false)} className="block px-4 py-2.5 text-sm text-gray-300 hover:bg-[#243B55] hover:text-white">{it.label}</Link>)}
                </Dropdown>
              )}
              <AppSwitcher user={user} compact />
              <Dropdown open={accountOpen} setOpen={setAccountOpen} width="w-56"
                button={
                  <span className="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-[#243B55]">
                    <span className="text-sm text-white font-medium truncate max-w-[120px] whitespace-nowrap">{user?.full_name}</span>
                    {getRoleBadge(user?.role, true)}
                    <span className="text-[10px] text-gray-400">▾</span>
                  </span>
                }>
                {user?.brokerage_name && user?.role !== 'buyer' && (
                  <div className="px-4 py-2.5 text-xs text-gray-400 border-b border-[#546E7A]/30 truncate">{user.brokerage_name}</div>
                )}
                <Link to="/change-password" onClick={() => setAccountOpen(false)} className="block px-4 py-2.5 text-sm text-gray-300 hover:bg-[#243B55] hover:text-white">Change Password</Link>
                <button onClick={() => { setAccountOpen(false); logout(); }} className="block w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-600/10 border-t border-[#546E7A]/30">Logout</button>
              </Dropdown>
            </div>

            {/* Mobile Hamburger (below lg only) */}
            <button className="lg:hidden flex flex-col justify-center items-center w-11 h-11 rounded-md hover:bg-[#243B55] shrink-0" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
              <span className="block w-6 h-0.5 bg-white mb-1.5"></span><span className="block w-6 h-0.5 bg-white mb-1.5"></span><span className="block w-6 h-0.5 bg-white"></span>
            </button>
          </div>

          {/* Mobile Dropdown Panel (below lg) */}
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-[#546E7A]/30 py-2">
              {navItems.map((item) => <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)} className={mobileCls(item.path)}>{item.label}</Link>)}
              {adminItems.length > 0 && (
                <div className="mt-1 pt-1 border-t border-[#546E7A]/20">
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-gray-500">Admin</div>
                  {adminItems.map((it) => <Link key={it.path} to={it.path} onClick={() => setMobileMenuOpen(false)} className={mobileCls(it.path)}>{it.label}</Link>)}
                </div>
              )}
              <div className="border-t border-[#546E7A]/30 mt-2 pt-3 px-3">
                <div className="text-sm text-white font-medium truncate">{user?.full_name}</div>
                <div className="flex items-center gap-2 mt-1 mb-3 flex-wrap">
                  {user?.brokerage_name && user?.role !== 'buyer' && <span className="text-xs text-gray-400 truncate max-w-full">{user.brokerage_name}</span>}
                  {getRoleBadge(user?.role)}
                  <AppSwitcher user={user} />
                </div>
                <Link to="/change-password" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-gray-300 hover:bg-[#243B55] hover:text-white rounded-md">Change Password</Link>
                <button onClick={() => { setMobileMenuOpen(false); logout(); }} className="block w-full text-left px-3 py-2.5 text-sm text-red-400 hover:bg-red-600/20 rounded-md">Logout</button>
              </div>
            </div>
          )}
        </div>

        {/* Zero-size clipped ghost row: measures each link's natural width for the overflow
            calc, without affecting page scroll width. */}
        <div aria-hidden="true" className="absolute top-0 left-0 w-0 h-0 overflow-hidden pointer-events-none" style={{ visibility: 'hidden' }}>
          <div ref={ghostRef} className="flex items-center gap-1" style={{ width: 'max-content' }}>
            {navItems.map((item) => <span key={item.path} data-gl className={linkCls(item.path)}>{item.label}</span>)}
            <span data-gm className={`${linkCls('/__more')} flex items-center gap-1`}>More <span className="text-[10px]">▾</span></span>
          </div>
        </div>
      </nav>
    </React.Fragment>
  );
};

export default Navigation;
